import { type JSX, type ReactNode, useEffect } from 'react';

import { store } from '@/app/store/store';
import { selectAccessToken, sessionCleared } from '@/features/auth';
import { registerAuthBridge } from '@/shared/api/client';

interface AuthBridgeProps {
  children: ReactNode;
}

/**
 * Conecta el cliente HTTP (axios) con el store de Redux para:
 *  - Inyectar el access token en cada request (cuando exista).
 *  - Reaccionar a 401 limpiando la sesión y redirigiendo a /login.
 *
 * Vive en `app/providers/` para evitar dependencia circular entre
 * `shared/api` y `app/store`. Se monta una sola vez bajo el StoreProvider.
 */
export function AuthBridge({ children }: AuthBridgeProps): JSX.Element {
  useEffect(() => {
    registerAuthBridge({
      getAccessToken: () => selectAccessToken(store.getState()),
      onUnauthorized: () => {
        store.dispatch(sessionCleared());
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          const next = encodeURIComponent(
            window.location.pathname + window.location.search,
          );
          window.location.assign(`/login?next=${next}`);
        }
      },
    });
  }, []);

  return <>{children}</>;
}
